import { content } from '../../content';
import { resolveCardDefinition } from '../../core/engine';
import type { GameState, RouteNodeKind } from '../../core/types';
import type { CommandHandler } from '../App';
import { GameHeader } from '../components/GameHeader';

import { ACT_DETAILS, routePresentation } from '../world';

function routeKindLabel(kind: RouteNodeKind): string {
  switch (kind) {
    case 'combat':
      return 'combat';
    case 'elite':
      return 'elite';
    case 'boss':
      return 'boss';
    case 'event':
      return 'event';
    case 'trader':
      return 'trader';
    case 'forge':
      return 'forge';
  }
}

export function RouteScreen({ state, onCommand }: { state: GameState; onCommand: CommandHandler }) {
  const run = state.run;
  if (!run) return null;

  const routeOptions = run.routeOptions;
  const gear = run.deck.filter((card) => content.cards[card.definitionId]?.gear);
  const brokenGear = gear.filter((card) => (card.durability ?? 0) === 0).length;
  const act = ACT_DETAILS[run.act] ?? { numeral: String(run.act), name: 'Beyond the line', note: 'Keep the evacuation route open.' };

  return (
    <div className="screen route-screen">
      <GameHeader state={state} eyebrow={act.name} />
      <main id="main-content" className="route-layout">
        <section className="route-intro" aria-labelledby="route-title">
          <p className="kicker">Act {act.numeral} · {act.name}</p>
          <h1 id="route-title">Choose your route</h1>
          <p>
            {act.note} Read the threat and inspect your Gear before committing.
          </p>
          <dl className="route-summary">
            <div><dt>Health</dt><dd>{run.hp}/{run.maxHp}</dd></div>
            <div><dt>Deck</dt><dd>{run.deck.length} cards</dd></div>
            <div><dt>Gear</dt><dd>{gear.length - brokenGear} ready</dd></div>
            <div><dt>Cleared</dt><dd>{run.combatsWon} fights</dd></div>
          </dl>
        </section>

        <section className="route-map" aria-labelledby="available-routes">
          <div className="section-heading">
            <span>
              <small>Route board</small>
              <h2 id="available-routes">Open passages</h2>
            </span>
            <span className="section-heading__note">Select an encounter to enter</span>
          </div>
          <div className="encounter-grid">
            {routeOptions.map((option, index) => {
              const copy = routePresentation(option);
              const encounter = option.encounterId ? content.encounters[option.encounterId] : undefined;
              const enemyNames = encounter?.enemies.map((id) => content.enemies[id]?.name ?? id) ?? [];
              return (
                <article className={`encounter-card encounter-card--${routeKindLabel(option.kind)}`} key={option.id}>
                  <span className="encounter-card__index" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                  <span className="encounter-card__kind">{routeKindLabel(option.kind)}</span>
                  <h3>{copy.label}</h3>
                  <p>{copy.description}</p>
                  <span className="encounter-card__enemies">
                    {enemyNames.length > 0 ? enemyNames.join(' · ') : 'Noncombat site'}
                  </span>
                  <button
                    type="button"
                    className="secondary-action"
                    onClick={() => onCommand({ type: 'choose_route', optionId: option.id })}
                  >
                    Enter {copy.label}
                  </button>
                </article>
              );
            })}
          </div>
        </section>

        <aside className="route-sidebar" aria-label="Expedition notes">
          <section className="field-note">
            <span className="field-note__number">{act.numeral}</span>
            <h2>Relics carried</h2>
            <ul className="route-relic-list">
              {run.relics.map((relicId) => {
                const relic = content.relics[relicId];
                return relic ? (
                  <li key={relic.id}>
                    <img className="relic-art relic-art--small" src={`/art/cathedris/relics/${relic.id}.webp`} alt="" />
                    <strong>{relic.name}</strong>
                    <span>{relic.description}</span>
                  </li>
                ) : null;
              })}
            </ul>
          </section>
          <section className="armory-note">
            <h2>Armory condition</h2>
            {gear.length > 0 ? (
              <ul>
                {gear.map((card) => {
                  const definition = resolveCardDefinition(card, content);
                  if (!definition?.gear) return null;
                  const current = card.durability ?? 0;
                  return (
                    <li key={card.id}>
                      <span>{definition.name}</span>
                      <strong className={current === 0 ? 'text-danger' : ''}>{current}/{definition.gear.maxDurability}</strong>
                    </li>
                  );
                })}
              </ul>
            ) : <p>No Gear in the deck.</p>}
          </section>
          <button className="text-action" type="button" onClick={() => onCommand({ type: 'abandon_run' })}>
            Abandon expedition
          </button>
        </aside>
      </main>
    </div>
  );
}
